import { isRejectedWithValue, type Middleware } from "@reduxjs/toolkit";
import { apiErrorMessage } from "@/lib/api-error";
import { toast } from "@/lib/toast";

/**
 * Turns a failed background load into a toast.
 *
 * One place, so every screen gets the same treatment and no screen has to
 * remember to ask for it. Before this, a query that failed was reported by
 * whatever inline state the page happened to have — which meant a page with a
 * skeleton and no error branch just sat there looking like it was still
 * loading.
 *
 * **Queries only.** A failed mutation is always tied to a control the person
 * just clicked, and the dialog or form beside that control already reports it
 * and keeps reporting it until they do something about it. A toast on top of
 * that is the same news twice, and the copy that vanishes after five seconds
 * is the one to drop.
 *
 * **401 and 403 are silent.** Those are not incidents, they are the session
 * ending or a permission being absent, and both are handled structurally — the
 * guard redirects to sign-in, the permission gate explains itself on the page.
 * A toast saying "Request failed (401)" on the way out to the sign-in screen
 * tells the user nothing they are about to not see anyway.
 *
 * **Offline is silent too.** Losing the network fails every request in flight
 * at once, and the panel already says so across the whole screen. Six toasts
 * behind that message would be six copies of what it is already telling them.
 */
export const apiErrorToast: Middleware = () => (next) => (action) => {
  if (!isRejectedWithValue(action)) return next(action);

  const meta = (action as { meta?: { arg?: { type?: string; endpointName?: string } } }).meta;
  if (meta?.arg?.type !== "query") return next(action);

  // The session load reports itself across the whole page area — see
  // `AppShell`. A toast on top of a panel that already says the same thing,
  // with a retry button on it, is the same news twice.
  if (meta.arg.endpointName === "getCurrentAdmin") return next(action);

  const status = (action.payload as { status?: unknown } | undefined)?.status;

  if (status === 401 || status === 403) return next(action);

  // Expected until the API ships it: the admin filter falls back to the old
  // list on its own, so there is nothing to tell anyone. See `AdminFilterSelect`.
  if (status === 404 && meta.arg.endpointName === "searchActivityActors") return next(action);
  if (typeof navigator !== "undefined" && !navigator.onLine) return next(action);

  const unreachable = status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";

  toast.error(unreachable ? "Can't reach the server" : "Couldn't load some of this page", {
    description: unreachable
      ? "Some of this page may be out of date. It will fill in once the connection is back."
      : apiErrorMessage(action.payload, "The server did not answer that request."),
  });

  return next(action);
};
